"use client";

/**
 * P2-02 · Drawer nav accordion (P2-DB03 r2 §3/§4): one level only — an L1
 * row expands in place to its L2 links (each straight to its category page)
 * plus "View all"; L3 never renders here and featured tiles are
 * desktop-only. Custom links interleave as plain rows. Independent
 * disclosures (several may be open at once — plain disclosure semantics).
 */
import * as React from "react";

import { LocaleLink } from "~/i18n/provider";
import type { NavTree } from "~/server/nav/compose-nav-tree";

export function MobileNavAccordion({
  items,
  onNavigate,
}: {
  items: NavTree;
  /** Called on any link navigation so the drawer can close. */
  onNavigate: () => void;
}) {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggle = (slug: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  return (
    <ul className="flex flex-col gap-0.5 text-sm">
      {items.map((item) =>
        item.kind === "link" ? (
          <li key={`${item.label}:${item.href}`}>
            <LocaleLink
              href={item.href}
              onClick={onNavigate}
              className="flex rounded-md px-2 py-2.5 font-medium text-fg hover:bg-surface-2"
            >
              {item.label}
            </LocaleLink>
          </li>
        ) : (
          <li key={item.slug}>
            <button
              type="button"
              aria-expanded={expanded.has(item.slug)}
              aria-controls={`drawer-section-${item.slug}`}
              onClick={() => toggle(item.slug)}
              className="flex w-full items-center justify-between rounded-md px-2 py-2.5 font-medium text-fg hover:bg-surface-2"
            >
              {item.label}
              <span aria-hidden="true" className="text-[10px] text-fg-muted">
                {expanded.has(item.slug) ? "▴" : "▾"}
              </span>
            </button>
            {expanded.has(item.slug) && (
              <ul
                id={`drawer-section-${item.slug}`}
                className="mb-1 flex flex-col gap-0.5 pl-3"
              >
                {item.columns.map((column) => (
                  <li key={column.href}>
                    <LocaleLink
                      href={column.href}
                      onClick={() => onNavigate()}
                      className="flex rounded-md px-2 py-2 text-fg-muted hover:bg-surface-2 hover:text-fg"
                    >
                      {column.label}
                    </LocaleLink>
                  </li>
                ))}
                <li>
                  <LocaleLink
                    href={item.viewAllHref}
                    onClick={() => onNavigate()}
                    className="flex rounded-md px-2 py-2 font-semibold text-primary hover:bg-surface-2"
                  >
                    View all {item.label}
                  </LocaleLink>
                </li>
              </ul>
            )}
          </li>
        ),
      )}
    </ul>
  );
}
